import { PopoverArrow } from "@repo/ui/popover/arrow";
import { popoverStyle } from "@repo/ui/popover/style";
import { IconAlertTriangle, IconSearch, IconTools } from "@tabler/icons-react";
import {
  useLayoutEffect,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from "react";
import {
  Button,
  Dialog,
  DialogTrigger,
  Header,
  Heading,
  Popover,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { PH_MASK_CLASS } from "../../features/analytics/sessionRecordingMasking.js";

const toolNameIconRules = [
  {
    keywords: ["look up", "lookup", "read", "search", "find"],
    icon: IconSearch,
  },
];

interface ToolCallActivityLineProps {
  status: "success" | "failed";
  label: ReactNode;
  title: string;
  formattedTime: string;
  /** The tool call's details, displayed in the popover. */
  children: ReactNode;
}

/** A tool call's summary and details. The popover masks itself outside the transcript. */
export const ToolCallActivityLine: FC<ToolCallActivityLineProps> = (props) => {
  const { status, label, title, formattedTime, children } = props;
  const labelRef = useRef<HTMLDivElement>(null);
  const [isMultiline, setIsMultiline] = useState(false);
  const isFailed = status === "failed";
  const normalizedToolName = title.toLowerCase().replace(/[_-]+/g, " ");
  const ToolIcon =
    toolNameIconRules.find((rule) =>
      rule.keywords.some((keyword) => normalizedToolName.includes(keyword)),
    )?.icon ?? IconTools;
  const ActivityIcon = isFailed ? IconAlertTriangle : ToolIcon;

  useLayoutEffect(() => {
    const labelElement = labelRef.current;
    if (!labelElement) return;

    const measureLabel = () => {
      const lineHeight = Number.parseFloat(
        getComputedStyle(labelElement).lineHeight,
      );
      // Exclude the added margins so they cannot keep multiline styling enabled.
      setIsMultiline(labelElement.offsetHeight > lineHeight);
    };

    measureLabel();
    const observer = new ResizeObserver(measureLabel);
    observer.observe(labelElement);

    return () => observer.disconnect();
  }, []);

  return (
    <DialogTrigger>
      <Button
        data-transcript-tool-call
        className={twMerge(
          "mx-auto flex w-fit max-w-full min-w-[min(100%,200px)] items-start gap-2 rounded-[12px] bg-transparent px-3 py-1 text-left text-xs leading-4 font-normal text-foreground select-none sm:max-w-[60%]",
          isFailed
            ? "text-red-700 hover:bg-red-100"
            : "hover:bg-[var(--tool-call-hover-bg,var(--color-accent))]",
        )}
      >
        <ActivityIcon
          className={twMerge(
            "size-3.5 shrink-0",
            isMultiline ? "mt-[7px]" : "mt-px",
            !isFailed && "text-muted-foreground",
          )}
        />
        <div
          ref={labelRef}
          className={twMerge(
            "line-clamp-2 min-w-0 wrap-anywhere",
            isMultiline && "my-1.5",
          )}
        >
          {label}
        </div>
      </Button>

      <Popover
        className={popoverStyle().popover({
          className: twMerge("flex min-w-0 flex-col", PH_MASK_CLASS),
        })}
        placement="top"
      >
        <PopoverArrow />
        <Dialog className="max-w-md space-y-4 overflow-y-auto px-4 py-3 text-sm">
          <Header>
            <Heading slot="title" className="truncate text-base font-semibold">
              {title}
            </Heading>
            <div className="text-gray-600">{formattedTime}</div>
          </Header>
          <div className="space-y-2">{children}</div>
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
};
